import { useEditor } from "@tiptap/react";
import type { Editor, Range } from "@tiptap/core";
import { useCallback, useEffect, useLayoutEffect, useRef } from "react";

import type { SkillDocumentJson } from "../../utils/skill-markdown-convert";
import { splitSkillMarkdown } from "../../utils/skill-frontmatter";
import {
  parseMarkdownToHtml,
  parseMarkdownToSlice,
  parseTextToSlice,
} from "../parse-markdown-to-slice";
import { getSkillMarkdownFrontmatterPlaceholder } from "../skill-markdown-slash-commands";
import { getSkillCollectionMarkdownExtensions } from "../skill-markdown-extensions";

import { cn } from "@/lib/utils";

const MARKDOWN_PLACEHOLDER = "Write your skill documentation…";

interface UseMarkdownEditorSyncParams {
  documentJson: SkillDocumentJson | undefined;
  fallbackMarkdown: string;
  hasImportedContent?: boolean;
  onClearImportFlag?: () => void;
  onDocumentChange: (doc: SkillDocumentJson) => void;
  onEditorReady?: (doc: SkillDocumentJson) => void;
  className?: string;
}

export const useMarkdownEditorSync = ({
  documentJson,
  fallbackMarkdown,
  hasImportedContent,
  onClearImportFlag,
  onDocumentChange,
  onEditorReady,
  className,
}: UseMarkdownEditorSyncParams) => {
  const frontmatterRef = useRef<string | null>(documentJson?.frontmatter ?? null);
  const onDocumentChangeRef = useRef(onDocumentChange);
  const onEditorReadyRef = useRef(onEditorReady);

  useEffect(() => {
    onDocumentChangeRef.current = onDocumentChange;
  }, [onDocumentChange]);

  useEffect(() => {
    onEditorReadyRef.current = onEditorReady;
  }, [onEditorReady]);

  useEffect(() => {
    if (documentJson) {
      frontmatterRef.current = documentJson.frontmatter;
    }
  }, [documentJson]);

  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const flushPendingUpdate = useCallback(() => {
    if (debounceTimerRef.current !== null) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
  }, []);

  const initialContent =
    documentJson?.bodyJson ?? parseMarkdownToHtml(splitSkillMarkdown(fallbackMarkdown).body);

  const handleInsertFrontmatter = useCallback(
    ({ editor, range }: { editor: Editor; range: Range }) => {
      flushPendingUpdate();
      editor.chain().focus().deleteRange(range).run();

      if (!frontmatterRef.current?.trim()) {
        frontmatterRef.current = getSkillMarkdownFrontmatterPlaceholder();
      }

      onDocumentChangeRef.current({
        frontmatter: frontmatterRef.current,
        bodyJson: editor.getJSON(),
      });
    },
    [flushPendingUpdate],
  );

  const editor = useEditor({
    extensions: getSkillCollectionMarkdownExtensions(MARKDOWN_PLACEHOLDER, {
      onInsertFrontmatter: handleInsertFrontmatter,
    }),
    content: initialContent,
    editorProps: {
      attributes: {
        class: cn(
          "skill-collection-markdown-editor prose prose-sm max-w-none px-4 py-3",
          "prose-headings:font-semibold prose-p:my-2 prose-ul:my-2 prose-ol:my-2",
          className,
        ),
      },
      clipboardTextParser: (text, $context) => parseMarkdownToSlice(text, $context.doc.type.schema),
      handlePaste: (view, event) => {
        const text = event.clipboardData?.getData("text/plain");
        const parsedDocument = text ? splitSkillMarkdown(text) : null;

        if (!parsedDocument?.frontmatter?.trim()) {
          return false;
        }

        event.preventDefault();
        flushPendingUpdate();
        frontmatterRef.current = parsedDocument.frontmatter;

        const replacement = parseTextToSlice(parsedDocument.body, view.state.schema);
        const transaction = view.state.tr.replaceSelection(replacement).scrollIntoView();
        const didChangeDocument = transaction.docChanged;

        view.dispatch(transaction);

        if (!didChangeDocument) {
          onDocumentChangeRef.current({
            frontmatter: frontmatterRef.current,
            bodyJson: view.state.doc.toJSON() as SkillDocumentJson["bodyJson"],
          });
        }

        return true;
      },
    },
    onCreate: ({ editor: instance }) => {
      onEditorReadyRef.current?.({
        frontmatter: frontmatterRef.current,
        bodyJson: instance.getJSON(),
      });
    },
    onUpdate: ({ editor: instance, transaction }) => {
      if (!transaction.docChanged) {
        return;
      }
      flushPendingUpdate();
      debounceTimerRef.current = setTimeout(() => {
        debounceTimerRef.current = null;
        onDocumentChangeRef.current({
          frontmatter: frontmatterRef.current,
          bodyJson: instance.getJSON(),
        });
      }, 300);
    },
  });

  useEffect(() => {
    if (!hasImportedContent || !editor || editor.isDestroyed || !documentJson) {
      return;
    }

    frontmatterRef.current = documentJson.frontmatter;
    editor.commands.setContent(documentJson.bodyJson, { emitUpdate: false });
    onClearImportFlag?.();
  }, [hasImportedContent, editor, documentJson, onClearImportFlag]);

  useLayoutEffect(() => {
    if (!editor || editor.isDestroyed) {
      return;
    }

    const persistDocument = onDocumentChange;

    return () => {
      if (editor.isDestroyed) {
        return;
      }
      flushPendingUpdate();
      persistDocument({
        frontmatter: frontmatterRef.current,
        bodyJson: editor.getJSON(),
      });
    };
  }, [editor, flushPendingUpdate, onDocumentChange]);

  return { editor };
};
